/**
 * MetersAndTariffsSection Component
 *
 * Project-level meter and tariff management.
 * Shows one card per meter (Facility Meter + optional EV Submeter),
 * each with its own tariff selector.
 */

import React, { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    FormControlLabel,
    Checkbox,
} from '@mui/material';
import { vortxColors, vortxTypography } from '../core/theme';
import MeterCard from './MeterCard';

/**
 * Get list of apps assigned to a given meter
 */
function getAppsOnMeter(project, meterId) {
    const apps = [];
    const appData = project?.apps || {};

    // Site DNA (could be array)
    const siteDna = Array.isArray(appData.site_dna)
        ? appData.site_dna
        : (appData.site_dna ? [appData.site_dna] : []);
    siteDna.forEach(sys => {
        if ((sys.meter || 'main') === meterId && sys.enabled !== false) {
            apps.push({ name: 'Site DNA', detail: sys.label || 'System' });
        }
    });

    // Flux EV
    if (appData.flux_ev?.enabled && (appData.flux_ev?.meter || 'main') === meterId) {
        const rel = appData.flux_ev.load_relationship || 'generates';
        apps.push({
            name: 'Flux EV',
            detail: rel === 'upload' ? 'uploaded load' : 'generates meter load',
        });
    }

    // Flux COLD
    if (appData.flux_cold?.enabled && (appData.flux_cold?.meter || 'main') === meterId) {
        const rel = appData.flux_cold.load_relationship || 'included';
        apps.push({
            name: 'Flux COLD',
            detail: rel === 'additive' ? 'additive load' : 'included in facility load',
        });
    }

    return apps;
}

function MetersAndTariffsSection({ project, onUpdateMeters, onUpdateAppConfig, onDecompose, siteAmps, serviceVoltage, onSiteAmpsChange, onServiceVoltageChange, onSiteElectricalSave }) {
    const meters = project?.meters || {};
    const mainMeter = meters.main || { name: 'Facility Meter', tariff: {} };
    const evSubmeter = meters.ev_submeter || null;

    const [hasEvSubmeter, setHasEvSubmeter] = useState(!!evSubmeter);

    // Sync checkbox with project data
    useEffect(() => {
        setHasEvSubmeter(!!meters.ev_submeter);
    }, [meters.ev_submeter]);

    const handleMeterTariffSelect = (meterId, tariff) => {
        const currentMeter = meters[meterId] || {};
        const meterName = currentMeter.name || (meterId === 'main' ? 'Facility Meter' : 'EV Submeter');
        onUpdateMeters({
            ...meters,
            [meterId]: {
                ...currentMeter,
                name: meterName,
                tariff: {
                    id: tariff.id || '',
                    name: tariff.name || '',
                    utility: tariff.utility_name || tariff.utility || '',
                    schedule: tariff.rate_schedule_name || '',
                    voltage: tariff.voltage_level || tariff.rate_data?.voltage_level || '',
                },
            },
        });
    };

    const handleEvSubmeterToggle = (checked) => {
        setHasEvSubmeter(checked);

        if (checked) {
            // Add EV submeter with empty tariff
            onUpdateMeters({
                ...meters,
                ev_submeter: {
                    name: 'EV Submeter',
                    tariff: {},
                },
            });
        } else {
            // Remove EV submeter
            const { ev_submeter, ...rest } = meters;
            onUpdateMeters(rest);
        }
    };

    const handleLoadProfileUpload = (meterId, loadProfileData) => {
        const currentMeter = meters[meterId] || { name: meterId === 'main' ? 'Facility Meter' : 'EV Submeter' };
        onUpdateMeters({
            ...meters,
            [meterId]: {
                ...currentMeter,
                load_profile: loadProfileData,
            },
        });
    };

    const handleLoadProfileRemove = (meterId) => {
        const currentMeter = meters[meterId] || {};
        const { load_profile, ...meterWithoutLoad } = currentMeter;
        onUpdateMeters({
            ...meters,
            [meterId]: meterWithoutLoad,
        });
    };

    return (
        <Box>
            <Typography
                variant="h5"
                sx={{
                    fontFamily: vortxTypography.heading,
                    color: vortxColors.white,
                    mb: 1,
                }}
            >
                Meters & Tariffs
            </Typography>

            <Typography
                variant="body2"
                sx={{
                    color: vortxColors.stone,
                    mb: 3,
                }}
            >
                Configure electricity tariffs for each meter at your facility. Each app inherits the tariff
                from its assigned meter.
            </Typography>

            {/* Facility Meter (always shown) */}
            <Box sx={{ mb: 3 }}>
                <MeterCard
                    meterId="main"
                    meter={mainMeter}
                    onTariffSelect={(tariff) => handleMeterTariffSelect('main', tariff)}
                    appsOnMeter={getAppsOnMeter(project, 'main')}
                    onLoadProfileUpload={handleLoadProfileUpload}
                    onLoadProfileRemove={handleLoadProfileRemove}
                    onAppConfigUpdate={onUpdateAppConfig}
                    project={project}
                    onDecompose={onDecompose}
                    siteAmps={siteAmps}
                    serviceVoltage={serviceVoltage}
                    onSiteAmpsChange={onSiteAmpsChange}
                    onServiceVoltageChange={onServiceVoltageChange}
                    onSiteElectricalSave={onSiteElectricalSave}
                />
            </Box>

            {/* EV Submeter Toggle */}
            <Box sx={{ mb: 3 }}>
                <FormControlLabel
                    control={
                        <Checkbox
                            checked={hasEvSubmeter}
                            onChange={(e) => handleEvSubmeterToggle(e.target.checked)}
                            sx={{
                                color: vortxColors.stone,
                                '&.Mui-checked': { color: vortxColors.gold },
                            }}
                        />
                    }
                    label={
                        <Typography variant="body2" sx={{ color: vortxColors.white }}>
                            This site has an EV submeter
                        </Typography>
                    }
                />
                <Typography variant="caption" sx={{ color: vortxColors.stone, display: 'block', ml: 4 }}>
                    Enable if EV charging has a separate meter with its own tariff.
                </Typography>
            </Box>

            {/* EV Submeter Card (if enabled) */}
            {hasEvSubmeter && (
                <Box sx={{ mb: 3 }}>
                    <MeterCard
                        meterId="ev_submeter"
                        meter={evSubmeter || { name: 'EV Submeter', tariff: {} }}
                        onTariffSelect={(tariff) => handleMeterTariffSelect('ev_submeter', tariff)}
                        appsOnMeter={getAppsOnMeter(project, 'ev_submeter')}
                        onLoadProfileUpload={handleLoadProfileUpload}
                        onLoadProfileRemove={handleLoadProfileRemove}
                        onAppConfigUpdate={onUpdateAppConfig}
                        project={project}
                    />
                </Box>
            )}
        </Box>
    );
}

export default MetersAndTariffsSection;
